import { useQuery } from '@tanstack/react-query';
import { Dialog } from 'sharedUi/Dialog';
import { Button } from 'sharedUi/Button';
import { usersService, type UserDependencies } from '../../services/users.service';

interface UserDeleteCascadeDialogProps {
  /** The user being deleted; null closes the dialog. */
  userId: string | null;
  onCancel: () => void;
  onConfirm: () => void;
  deleting?: boolean;
  testId?: string;
}

// A single "will be archived" group with a readable chip list.
const CascadeGroup = ({ title, items }: { title: string; items: string[] }) => {
  if (!items.length) return null;
  return (
    <div className="rounded-md border border-gray-200 bg-white p-2.5 dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
          {title}
        </span>
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
          {items.length}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((label, i) => (
          <span
            key={`${label}-${i}`}
            className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 font-mono text-[11px] text-gray-700 dark:bg-gray-700 dark:text-gray-200"
          >
            {label}
          </span>
        ))}
      </div>
    </div>
  );
};

/**
 * Delete dialog for a user that PREVIEWS the cascade before it runs.
 * Unlike a blocking dependency delete, a user delete is never refused — instead it
 * archives the user's role assignments + devices and revokes their sessions. This
 * dialog lists exactly what will be archived so the admin approves the cascade first.
 * Mirrors the backend delete_user, which archives the same records.
 */
const UserDeleteCascadeDialog = ({
  userId,
  onCancel,
  onConfirm,
  deleting = false,
  testId = 'UserDeleteCascadeDialog',
}: UserDeleteCascadeDialogProps) => {
  const { data, isLoading, isError } = useQuery<UserDependencies>({
    queryKey: ['users', 'dependencies', userId],
    queryFn: () => usersService.getDependencies(userId as string),
    enabled: !!userId,
    staleTime: 0,
  });

  const d = data?.dependents;
  const hasCascade = !!data && data.total > 0;

  const footer = (
    <div className="flex justify-end gap-3">
      <Button
        label="Cancel"
        severity="secondary"
        outlined
        size="small"
        onClick={onCancel}
        disabled={deleting}
      />
      <Button
        label={deleting ? 'Deleting...' : hasCascade ? `Delete & archive ${data!.total} item(s)` : 'Delete'}
        severity="danger"
        size="small"
        onClick={onConfirm}
        disabled={deleting || isLoading || isError}
        icon={deleting ? 'pi pi-spin pi-spinner' : undefined}
      />
    </div>
  );

  return (
    <Dialog
      visible={!!userId}
      onHide={onCancel}
      header="Delete user"
      footer={footer}
      style={{ width: '480px', maxWidth: '95vw' }}
    >
      <div data-testid={testId}>
        {isLoading ? (
          <div className="flex items-center gap-2 py-4 text-sm text-ink-muted">
            <i className="pi pi-spin pi-spinner" />
            Checking what will be affected...
          </div>
        ) : isError ? (
          <div className="flex items-start gap-3">
            <i className="pi pi-exclamation-triangle flex-shrink-0 text-2xl text-amber-500" />
            <p className="m-0 text-sm leading-relaxed text-ink-muted">
              Could not check dependencies. Please try again.
            </p>
          </div>
        ) : hasCascade && d ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-start gap-3">
              <i className="pi pi-exclamation-triangle flex-shrink-0 text-2xl text-amber-500" />
              <p className="m-0 text-sm leading-relaxed text-ink-muted">
                Deleting this user will also <strong>archive</strong> the records below and revoke
                their active sessions. This cannot be undone. Continue?
              </p>
            </div>
            <CascadeGroup
              title="Role Assignments"
              items={d.roleAssignments.map((x) => `${x.role} @ ${x.unit}`)}
            />
            <CascadeGroup
              title="Devices"
              items={d.devices.map((x) => x.deviceName || x.deviceId || x._id)}
            />
            {d.sessions > 0 && (
              <CascadeGroup title="Active Sessions" items={[`${d.sessions} session(s) will be revoked`]} />
            )}
          </div>
        ) : (
          <div className="flex items-start gap-3">
            <i className="pi pi-exclamation-triangle flex-shrink-0 text-2xl text-amber-500" />
            <p className="m-0 text-sm leading-relaxed text-ink-muted">
              Nothing else depends on this user. Are you sure you want to delete it? This action
              cannot be undone.
            </p>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default UserDeleteCascadeDialog;
